import type { components } from '@/generated/internal-api/openapi-types';
import { _isNotBlank } from '@/littledash';

type ExternalColonyImportResponseV1 = components['schemas']['ExternalColonyImportResponseV1.schema'];

export type ExcludedAnimalError = NonNullable<ExternalColonyImportResponseV1['excluded_animal_errors']>[number];

export interface ExcludedAnimalErrorGroup {
  message: string;
  animalCount: number;
}

/**
 * Above this many excluded animals the individual per-animal breakdown becomes unreadable,
 * so the errors are aggregated by message instead.
 */
export const EXCLUDED_ANIMAL_DETAIL_LIMIT = 5;

/**
 * Flattens every excluded animal error into per-message groups holding the number of animals
 * the message applied to, ordered from the most to the least frequent. Messages with an equal
 * count keep the order in which the API returned them, and a message repeated for the same
 * animal is only counted once.
 */
export const groupExcludedAnimalErrors = (
  excludedAnimalErrors: Array<ExcludedAnimalError>
): Array<ExcludedAnimalErrorGroup> => {
  const countByMessage = excludedAnimalErrors.reduce<Map<string, number>>((acc, { errors }) => {
    new Set(errors ?? []).forEach((message) => {
      if (_isNotBlank(message)) {
        acc.set(message, (acc.get(message) ?? 0) + 1);
      }
    });
    return acc;
  }, new Map());

  return Array.from(countByMessage, ([message, animalCount]) => ({ message, animalCount })).sort(
    (a, b) => b.animalCount - a.animalCount
  );
};
